import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { CheckCircle2 } from 'lucide-react';
import { IpeBrandMark } from './IpeBrandLogo';
import IpeLoginForm from './IpeLoginForm';
import IpeSmsCodeField, { phonePattern } from './IpeSmsCodeField';
import AuthPasswordFields, { isPasswordFormValid } from './AuthPasswordFields';
import { IPE_PASSWORD_REQUIREMENT_TEXT } from '@/utils/ipePasswordValidation';
import { buildLoginContext, prefetchLoginContext } from '@/utils/deviceLocation';

const AUTH_ASIDE_BG = `${process.env.PUBLIC_URL || ''}/images/ipe/login/auth-aside-bg.png`;

function IpeAuthAsideContent() {
  const { t } = useTranslation('login');
  return (
    <div className="ipe-auth-modal__aside-content">
      <h3 className="ipe-auth-modal__aside-title">
        {t('auth.asideTitle1')}
        <br />
        {t('auth.asideTitle2')}
      </h3>
      <p className="ipe-auth-modal__aside-lead">
        {t('auth.asideLead1')}
        <br />
        {t('auth.asideLead2')}
      </p>
    </div>
  );
}

function IpeLogoMark({ className = '' }) {
  const { t } = useTranslation('login');
  return (
    <IpeBrandMark
      className={`ipe-auth-modal__logo${className ? ` ${className}` : ''}`}
      textClassName="ipe-auth-modal__logo-text"
      text={t('auth.brand')}
    />
  );
}

function ResetPasswordPanel({
  onSwitchToLogin,
  onResetPassword,
  onSendVerificationCode,
  submitting = false,
}) {
  const { t } = useTranslation('login');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const validate = () => {
    if (!phonePattern.test(phone.trim())) return t('auth.badPhone');
    if (!/^\d{6}$/.test(code.trim())) return t('auth.badCode');
    if (!isPasswordFormValid(password, confirmPassword)) return null;
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setError('');
    if (!isPasswordFormValid(password, confirmPassword)) {
      return;
    }
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    try {
      const data = await onResetPassword(phone.trim(), code.trim(), password);
      setSuccessMessage(data?.message || t('auth.resetDoneDetail'));
    } catch (err) {
      const message = err?.message || t('auth.resetFailed');
      if (message === IPE_PASSWORD_REQUIREMENT_TEXT || message.includes('密码至少')) {
        setSubmitAttempted(true);
      } else {
        setError(message);
      }
    }
  };

  if (successMessage) {
    return (
      <div className="ipe-auth-modal__form-area">
        <div className="ipe-auth-modal__success-panel">
          <div className="ipe-auth-modal__success-icon" aria-hidden="true">
            <CheckCircle2 size={48} strokeWidth={1.75} />
          </div>
          <h2 className="ipe-auth-modal__success-title">{t('auth.resetDone')}</h2>
          <p className="ipe-auth-modal__success-text">{successMessage}</p>
          <button type="button" className="ipe-auth-modal__submit" onClick={onSwitchToLogin}>
            {t('auth.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ipe-auth-modal__form-area">
      <div className="ipe-auth-modal__form-body">
        <h2 className="ipe-auth-modal__form-title">{t('auth.forgotTitle')}</h2>
        <p className="ipe-auth-modal__form-subtitle">{t('auth.forgotSub')}</p>

        <form className="ipe-auth-modal__trial-form" onSubmit={handleSubmit}>
          <div className="ipe-auth-modal__field">
            <input
              type="tel"
              placeholder={t('auth.phone')}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
              maxLength={11}
              disabled={submitting}
            />
          </div>

          <IpeSmsCodeField
            phone={phone}
            scene="reset_password"
            value={code}
            onChange={setCode}
            onSendCode={onSendVerificationCode}
            disabled={submitting}
          />

          <AuthPasswordFields
            password={password}
            confirmPassword={confirmPassword}
            onPasswordChange={setPassword}
            onConfirmChange={setConfirmPassword}
            showPassword={showPassword}
            onToggleShowPassword={() => setShowPassword((v) => !v)}
            disabled={submitting}
            submitAttempted={submitAttempted}
            passwordPlaceholder={t('auth.newPassword')}
            confirmPlaceholder={t('auth.confirmNewPassword')}
          />

          {error ? <p className="ipe-auth-modal__error" role="alert">{error}</p> : null}

          <button type="submit" className="ipe-auth-modal__submit" disabled={submitting}>
            {submitting ? t('auth.submitting') : t('auth.resetPassword')}
          </button>
        </form>
      </div>

      <p className="ipe-auth-modal__switch">
        {t('auth.remembered')}
        <button type="button" className="ipe-auth-modal__switch-btn" onClick={onSwitchToLogin}>
          {t('auth.loginNow')}
        </button>
      </p>
    </div>
  );
}

function RegisterFormPanel({
  onSwitchToLogin,
  onClose,
  onRegisterSubmit,
  onSendVerificationCode,
  submitting = false,
}) {
  const { t } = useTranslation('login');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [phase, setPhase] = useState('form');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const phoneValid = useMemo(() => phonePattern.test(phone.trim()), [phone]);

  useEffect(() => {
    setPhase('form');
    setPhone('');
    setPassword('');
    setConfirmPassword('');
    setNickname('');
    setCode('');
    setError('');
    setSuccessMessage('');
    setSubmitAttempted(false);
  }, []);

  const validate = () => {
    if (!phoneValid) return t('auth.badPhone');
    if (!nickname.trim()) return t('auth.nicknameRequired');
    if (!/^\d{6}$/.test(code.trim())) return t('auth.badCode');
    if (!isPasswordFormValid(password, confirmPassword)) return null;
    if (!agreed) return t('auth.needTerms');
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setError('');
    if (!isPasswordFormValid(password, confirmPassword)) {
      return;
    }
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    try {
      const loginContext = buildLoginContext(true);
      const data = await onRegisterSubmit(
        phone.trim(),
        password,
        nickname.trim(),
        code.trim(),
        loginContext,
      );
      setSuccessMessage(data?.message || t('auth.registerOkDetail'));
      setPhase('success');
    } catch (err) {
      const message = err?.message || t('auth.registerFailed');
      if (message === IPE_PASSWORD_REQUIREMENT_TEXT || message.includes('密码至少')) {
        setSubmitAttempted(true);
      } else {
        setError(message);
      }
    }
  };

  if (phase === 'success') {
    return (
      <div className="ipe-auth-modal__form-area">
        <div className="ipe-auth-modal__success-panel">
          <div className="ipe-auth-modal__success-icon" aria-hidden="true">
            <CheckCircle2 size={48} strokeWidth={1.75} />
          </div>
          <h2 className="ipe-auth-modal__success-title">{t('auth.registerOk')}</h2>
          <p className="ipe-auth-modal__success-text">{successMessage}</p>
          <button type="button" className="ipe-auth-modal__submit" onClick={onClose}>
            {t('auth.enterWorkbench')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ipe-auth-modal__form-area">
      <div className="ipe-auth-modal__form-body">
        <h2 className="ipe-auth-modal__form-title">{t('auth.registerTitle')}</h2>
        <p className="ipe-auth-modal__form-subtitle">{t('auth.registerSub')}</p>

        <form className="ipe-auth-modal__trial-form" onSubmit={handleSubmit}>
          <div className="ipe-auth-modal__field">
            <input
              type="text"
              placeholder={t('auth.nickname')}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={64}
              disabled={submitting}
            />
          </div>
          <div className="ipe-auth-modal__field">
            <input
              type="tel"
              placeholder={t('auth.phone')}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
              maxLength={11}
              disabled={submitting}
            />
          </div>

          <IpeSmsCodeField
            phone={phone}
            scene="register"
            value={code}
            onChange={setCode}
            onSendCode={onSendVerificationCode}
            disabled={submitting}
          />

          <AuthPasswordFields
            password={password}
            confirmPassword={confirmPassword}
            onPasswordChange={setPassword}
            onConfirmChange={setConfirmPassword}
            showPassword={showPassword}
            onToggleShowPassword={() => setShowPassword((v) => !v)}
            disabled={submitting}
            submitAttempted={submitAttempted}
          />

          <label className="ipe-login-form__terms ipe-login-form__terms--modal">
            <input
              type="checkbox"
              className="ipe-login-form__terms-checkbox"
              checked={agreed}
              onChange={(e) => {
                const checked = e.target.checked;
                setAgreed(checked);
                if (checked) {
                  prefetchLoginContext();
                }
              }}
              disabled={submitting}
            />
            <span className="ipe-login-form__terms-text">
              {t('auth.agreePrefix')}
              <a href="/legal/terms" className="ipe-login-form__terms-link" target="_blank" rel="noopener noreferrer">{t('auth.terms')}</a>
              {t('auth.agreeAnd')}
              <a href="/legal/privacy" className="ipe-login-form__terms-link" target="_blank" rel="noopener noreferrer">{t('auth.privacy')}</a>
            </span>
          </label>

          {error ? <p className="ipe-auth-modal__error" role="alert">{error}</p> : null}

          <button type="submit" className="ipe-auth-modal__submit" disabled={submitting}>
            {submitting ? t('auth.registering') : t('auth.registerNow')}
          </button>
        </form>
      </div>

      <p className="ipe-auth-modal__switch">
        {t('auth.hasAccount')}
        <button type="button" className="ipe-auth-modal__switch-btn" onClick={onSwitchToLogin}>
          {t('auth.loginNow')}
        </button>
      </p>
    </div>
  );
}

export default function IpeAuthModal({
  open,
  mode = 'login',
  onModeChange,
  onClose,
  onLoginSubmit,
  onSmsLoginSubmit,
  onWeChatLoginSuccess,
  onRegisterSubmit,
  onResetPasswordSubmit,
  onSendVerificationCode,
  loginSubmitting = false,
  registerSubmitting = false,
  resetPasswordSubmitting = false,
}) {
  const { t } = useTranslation('login');
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const renderMain = () => {
    if (mode === 'register') {
      return (
        <RegisterFormPanel
          onSwitchToLogin={() => onModeChange?.('login')}
          onClose={onClose}
          onRegisterSubmit={onRegisterSubmit}
          onSendVerificationCode={onSendVerificationCode}
          submitting={registerSubmitting}
        />
      );
    }
    if (mode === 'reset-password') {
      return (
        <ResetPasswordPanel
          onSwitchToLogin={() => onModeChange?.('login')}
          onResetPassword={onResetPasswordSubmit}
          onSendVerificationCode={onSendVerificationCode}
          submitting={resetPasswordSubmitting}
        />
      );
    }
    return (
      <IpeLoginForm
        variant="modal"
        onSubmit={onLoginSubmit}
        onSmsSubmit={onSmsLoginSubmit}
        onWeChatSuccess={onWeChatLoginSuccess}
        onSendVerificationCode={onSendVerificationCode}
        submitting={loginSubmitting}
        onSwitchToRegister={() => onModeChange?.('register')}
        onForgotPassword={() => onModeChange?.('reset-password')}
      />
    );
  };

  return createPortal(
    <div className="ipe-auth-modal" role="presentation" onClick={onClose}>
      <button type="button" className="ipe-auth-modal__close" onClick={onClose} aria-label={t('auth.close')}>
        ×
      </button>

      <div
        className="ipe-auth-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ipe-auth-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <aside
          className="ipe-auth-modal__aside"
          aria-hidden="false"
          style={{ '--ipe-auth-aside-bg': `url("${AUTH_ASIDE_BG}")` }}
        >
          <IpeLogoMark />
          <IpeAuthAsideContent />
        </aside>

        <div className="ipe-auth-modal__main">
          {renderMain()}
        </div>
      </div>
    </div>,
    document.body,
  );
}
